const PAGINATION_SELECTOR = '.search-page__pagination, [data-search-pagination]';
const ARROW_SELECTOR = '.search-page__pagination-arrow';
const PAGE_BUTTON_SELECTOR = '.search-page__pagination-button, [data-page]';
const ACTIVE_PAGE_SELECTOR =
  '.search-page__pagination-button--active, .search-page__pagination-button[aria-current="page"], [data-page][aria-current="page"]';

const toPositiveInteger = (value) => {
  const parsedValue = Number.parseInt(String(value ?? '').trim(), 10);

  if (!Number.isInteger(parsedValue) || parsedValue < 1) {
    return null;
  }

  return parsedValue;
};

const getPageNumber = (element) => {
  if (!(element instanceof HTMLElement)) {
    return null;
  }

  return toPositiveInteger(element.dataset.page ?? element.textContent);
};

const getPaginationArrows = (pagination) => {
  const arrows = Array.from(pagination.querySelectorAll(ARROW_SELECTOR)).filter(
    (element) => element instanceof HTMLElement
  );

  return {
    prev:
      pagination.querySelector('[data-page-arrow="prev"]') instanceof HTMLElement
        ? pagination.querySelector('[data-page-arrow="prev"]')
        : (arrows[0] ?? null),
    next:
      pagination.querySelector('[data-page-arrow="next"]') instanceof HTMLElement
        ? pagination.querySelector('[data-page-arrow="next"]')
        : (arrows.at(-1) ?? null),
  };
};

const setDisabledState = (element, isDisabled) => {
  if (!(element instanceof HTMLElement)) {
    return;
  }

  element.setAttribute('aria-disabled', String(isDisabled));

  if (element instanceof HTMLButtonElement) {
    element.disabled = isDisabled;
    return;
  }

  if (isDisabled) {
    element.setAttribute('tabindex', '-1');
  } else {
    element.removeAttribute('tabindex');
  }
};

const syncPaginationState = (pagination) => {
  if (!(pagination instanceof HTMLElement) || pagination.hidden) {
    return;
  }

  const pageButtons = Array.from(pagination.querySelectorAll(PAGE_BUTTON_SELECTOR)).filter(
    (element) => element instanceof HTMLElement
  );
  const pageNumbers = pageButtons.map(getPageNumber).filter((value) => value !== null);
  const totalPages = Math.max(1, ...pageNumbers);
  const activePageElement = pagination.querySelector(ACTIVE_PAGE_SELECTOR);
  const activePage = getPageNumber(activePageElement);
  const urlPage = toPositiveInteger(new URLSearchParams(window.location.search).get('page'));
  const currentPage = Math.min(Math.max(activePage ?? urlPage ?? 1, 1), totalPages);
  const { prev, next } = getPaginationArrows(pagination);

  setDisabledState(prev, currentPage <= 1);
  setDisabledState(next, currentPage >= totalPages);
};

export const initSearchPagination = () => {
  const searchPage = document.querySelector('.search-page');

  if (!(searchPage instanceof HTMLElement)) {
    return;
  }

  const sync = () => {
    const pagination = searchPage.querySelector(PAGINATION_SELECTOR);

    if (!(pagination instanceof HTMLElement)) {
      return;
    }

    syncPaginationState(pagination);
  };

  searchPage.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const disabledArrow = event.target.closest(`${ARROW_SELECTOR}[aria-disabled="true"]`);

    if (!disabledArrow) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  });

  const observer = new MutationObserver(() => {
    sync();
  });

  observer.observe(searchPage, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-current', 'class', 'data-page', 'hidden'],
  });

  window.addEventListener('popstate', sync);

  sync();
};
